import { practiceState } from "../fuxi/yufa-practice.js";
import {
  exerciseAttribute,
  freeAttribute,
  handedOverAttribute,
  missedAttribute,
  queueAttribute,
  recordAttribute,
  versionAttribute,
  revisionAttribute,
  itemNameAttribute,
} from "../content/lianxi/exercise-fields.js";
import type { KeptQueue } from "./lianxi-yufa-queue.js";
import { YufaRecorder } from "./lianxi-yufa-recorder.js";
import { blockHandoverCount } from "./lianxi-handover.js";
import { said } from "./xiezi-parts.js";

/**
 * What a grammar exercise block says to the script beyond its items: where
 * the reader is, and where a press posts.
 *
 * `src/user/lianxi-yufa-markup.ts` puts the kept queue and the missed items
 * on the block as lists of indexes and names where to post, and
 * `lianxi-yufa-start.ts` reads all of it through this. The public block
 * names none of it. `lianxi-block-data.ts` is the same for a writing drill.
 */

/** A list of whole numbers off an attribute, as `3,0,5`. */
function indexes(value: string): readonly number[] {
  return value === ""
    ? []
    : value
        .split(",")
        .map((each) => Number(each))
        .filter((at) => Number.isInteger(at) && at >= 0);
}

/** What the handler's page kept, or nothing for the public block. */
export interface KeptBlock {
  readonly kept: KeptQueue;
  readonly keeper: YufaRecorder;
}

/**
 * The kept queue and the recorder for a block that names where to post, or
 * nothing for the public block, which keeps no place.
 * @param block - The block.
 * @param fetching - How to post.
 * @returns What was kept, or nothing.
 */
export function keptBlock(
  block: HTMLElement,
  fetching: typeof fetch,
): KeptBlock | undefined {
  const address = said(block, recordAttribute);
  const slug = said(block, exerciseAttribute);

  if (address === "" || slug === "") {
    return undefined;
  }

  const practice = practiceState(
    JSON.parse(block.dataset["cbYufaPractice"] ?? "null") as unknown,
    block.querySelectorAll("form[data-cb-yufa-item]").length,
  );
  return {
    kept: {
      ...(practice === undefined ? {} : { practice }),
      queue: indexes(said(block, queueAttribute)),
      missed: indexes(said(block, missedAttribute)),
    },
    keeper: new YufaRecorder({
      address,
      slug,
      fetch: fetching,
      version: block.dataset[versionAttribute],
      revision:
        block.dataset[revisionAttribute] === undefined
          ? undefined
          : Number(block.dataset[revisionAttribute]),
      items: [
        ...block.querySelectorAll<HTMLElement>("form[data-cb-yufa-item]"),
      ].map((form) => form.dataset[itemNameAttribute] ?? ""),
      handedOver: block.dataset[handedOverAttribute] !== undefined,
      handoverCount: blockHandoverCount(block),
      free: block.dataset[freeAttribute] !== undefined,
      commented: block.dataset["cbYufaCommented"] === "true",
    }),
  };
}
